import { CheckIcon, LabelChip } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { displayVideoResolutions, type DatasetStructureChannel } from "@/api";
import { CHANNEL_TOPIC_ORDER, channelTopicLabel } from "./channelTopicTerms";
import { formatVideoResolutions } from "./datasetFormatters";
import styles from "./ChannelStructureGroups.module.css";

// LabelChip은 숫자 accent를 필수로 받는다. 채널마다 임의 색을 만들지 않는다.
// 모든 채널이 c6 기본 계열 하나를 쓴다.
const CHANNEL_CHIP_COLOR = 0x3366cc;

type ChannelStructureGroupsBaseProps = {
  channels: readonly DatasetStructureChannel[];
  episodeCount: number;
  topicOrder?: readonly string[];
};

type ChannelStructureGroupsStaticProps = ChannelStructureGroupsBaseProps & {
  mode: "static";
  // 카메라 칩에 영상 해상도를 붙인다. 데이터셋 자체를 설명하는 자리(데이터셋 상세)만 켠다.
  showVideoResolution?: boolean;
  // 메타정보에서는 각 topic을 라벨과 칩의 한 행으로 표시한다.
  compact?: boolean;
};

type ChannelStructureGroupsSelectableProps = ChannelStructureGroupsBaseProps & {
  mode: "selectable";
  selectedNames: Readonly<Partial<Record<string, readonly string[]>>>;
  fixedChannelKeys?: ReadonlySet<string>;
  disabled?: boolean;
  onToggle: (topic: string, name: string) => void;
};

export type ChannelStructureGroupsProps =
  | ChannelStructureGroupsStaticProps
  | ChannelStructureGroupsSelectableProps;

// 이 컴포넌트는 채널 topic의 표시 순서와 static/selectable DOM 계약을 소유한다.
// 데이터 선택·제출 정책은 소유하지 않는다. 호출자가 전달한 선택 상태와 fixed key를
// 시각 상태로 표현만 한다.
export function ChannelStructureGroups(props: ChannelStructureGroupsProps): ReactNode {
  const topics = orderedTopics(props.channels, props.topicOrder ?? CHANNEL_TOPIC_ORDER);
  // 액션·보조 벡터는 칩이 적다. 세로 공간을 줄이려고 둘 다 있을 때만 한 행에 나란히 둔다.
  // 하나만 있으면 반 폭 옆에 빈 칸이 남는다. 그래서 전폭을 유지한다.
  const pairActionRow = topics.includes("action") && topics.includes("auxiliary_vector");
  return (
    <div
      className={styles.groups}
      data-compact={props.mode === "static" && props.compact ? "true" : undefined}
    >
      {topics.map((topic) => {
        const channels = props.channels.filter((channel) => channel.topic === topic);
        const selectedNames =
          props.mode === "selectable" ? new Set(props.selectedNames[topic] ?? []) : null;
        const selectedCount =
          props.mode === "selectable"
            ? channels.filter(
                (channel) =>
                  selectedNames?.has(channel.name) ||
                  props.fixedChannelKeys?.has(channelKey(topic, channel.name)),
              ).length
            : 0;
        const topicLabel = channelTopicLabel(topic);
        const half = pairActionRow && (topic === "action" || topic === "auxiliary_vector");

        return (
          <div
            key={topic}
            className={[styles.topicGroup, half ? styles.topicGroupHalf : ""]
              .filter(Boolean)
              .join(" ")}
            role="group"
            aria-label={`${topicLabel} 채널`}
          >
            <div className={styles.topicHeader}>
              <span className={styles.topicLabel}>{topicLabel}</span>
              <span className={styles.topicSummary}>
                {props.mode === "selectable"
                  ? `선택 ${selectedCount}/${channels.length}`
                  : `${channels.length}개`}
              </span>
            </div>
            <div className={styles.chips}>
              {channels.map((channel) => (
                <ChannelItem
                  key={channel.name}
                  channel={channel}
                  episodeCount={props.episodeCount}
                  topic={topic}
                  props={props}
                  selected={selectedNames?.has(channel.name) ?? false}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ChannelItem({
  channel,
  episodeCount,
  topic,
  props,
  selected,
}: {
  channel: DatasetStructureChannel;
  episodeCount: number;
  topic: string;
  props: ChannelStructureGroupsProps;
  selected: boolean;
}): ReactNode {
  const fixed =
    props.mode === "selectable" &&
    (props.fixedChannelKeys?.has(channelKey(topic, channel.name)) ?? false);
  const interactive = props.mode === "selectable" && !fixed;
  const isSelected = selected || fixed;
  const showVideoResolution = props.mode === "static" && (props.showVideoResolution ?? false);
  const label = channelLabel(channel, { episodeCount, fixed, showVideoResolution });

  return (
    <LabelChip
      color={CHANNEL_CHIP_COLOR}
      size="xs"
      // c6 LabelChip 은 표시 방식을 아직 `variant` 로 받는다. 이 줄이 앱 `mode` 를 `variant` 로 바꾼다.
      variant={interactive ? "interactive" : fixed ? "filled" : "outline"}
      icon={isSelected ? <CheckIcon /> : undefined}
      showIcon={false}
      aria-pressed={interactive ? isSelected : undefined}
      disabled={interactive ? props.disabled : undefined}
      title={fixed ? `${label} 채널은 서버가 자동으로 포함합니다.` : `${label} 채널입니다.`}
      onClick={interactive ? () => props.onToggle(topic, channel.name) : undefined}
    >
      {label}
    </LabelChip>
  );
}

function channelLabel(
  channel: DatasetStructureChannel,
  {
    episodeCount,
    fixed,
    showVideoResolution,
  }: { episodeCount: number; fixed: boolean; showVideoResolution: boolean },
): string {
  const parts = [channel.name];
  if (channel.dim !== null) {
    parts.push(`${channel.dim}D`);
  }
  const resolutions = showVideoResolution
    ? displayVideoResolutions(channel.video_resolution)
    : null;
  if (resolutions !== null) {
    parts.push(formatVideoResolutions(resolutions));
  }
  if (fixed) {
    parts.push("자동");
  }
  if (episodeCount > 0 && channel.episode_count < episodeCount) {
    parts.push(`일부 ${channel.episode_count}/${episodeCount} ep`);
  }
  return parts.join(" · ");
}

function orderedTopics(
  channels: readonly DatasetStructureChannel[],
  preferredOrder: readonly string[],
): string[] {
  const presentTopics = [...new Set(channels.map((channel) => channel.topic))];
  const preferred = preferredOrder.filter((topic) => presentTopics.includes(topic));
  const remaining = presentTopics.filter((topic) => !preferred.includes(topic));
  return [...preferred, ...remaining];
}

function channelKey(topic: string, name: string): string {
  return `${topic}/${name}`;
}
